import { useCallback, useEffect, useRef, useState } from 'react';

type ConnectionStatus = 'connecting' | 'connected' | 'disconnected' | 'error';

interface IncomingMessage {
  type: string;
  bot_name?: string;
  unread_count?: number;
  count?: number;
  message?: string;
  timestamp?: string;
}

interface UseUserWebSocketReturn {
  status: ConnectionStatus;
  isConnected: boolean;
  subscribe: (botName: string) => void;
  unsubscribe: (botName: string) => void;
  markRead: (botName: string, lastMessageId?: string | number) => void;
  onUnreadCountUpdate?: (botName: string, count: number) => void;
  onNewMessage?: (message: any) => void;
}

export const useUserWebSocket = (
  userId: number | null,
  onUnreadCountUpdate?: (botName: string, count: number) => void,
  onNewMessage?: (message: any) => void
): UseUserWebSocketReturn => {
  const [status, setStatus] = useState<ConnectionStatus>('disconnected');
  const [isConnected, setIsConnected] = useState(false);
  const wsRef = useRef<WebSocket | null>(null);
  const reconnectAttemptsRef = useRef(0);
  const reconnectTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const maxReconnectAttempts = 5;

  const buildWsUrl = (): string | null => {
    try {
      const protocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
      const host = process.env.REACT_APP_API_URL?.replace(/^https?:\/\//, '') || '';
      if (!host || !userId) return null;
      const cleanHost = host.replace(/\/api$/, '');
      return `${protocol}//${cleanHost}/api/ws/user/${userId}`;
    } catch (e) {
      return null;
    }
  };

  const send = useCallback((payload: any) => {
    const ws = wsRef.current;
    if (ws && ws.readyState === WebSocket.OPEN) {
      ws.send(JSON.stringify(payload));
    }
  }, []);

  const subscribe = useCallback((botName: string) => {
    send({ type: 'subscribe', bot_name: botName });
  }, [send]);

  const unsubscribe = useCallback((botName: string) => {
    send({ type: 'unsubscribe', bot_name: botName });
  }, [send]);

  const markRead = useCallback((botName: string, lastMessageId?: string | number) => {
    const payload: any = { type: 'mark_read', bot_name: botName };
    if (lastMessageId !== undefined) payload.last_message_id = lastMessageId;
    send(payload);
  }, [send]);

  const connect = useCallback(async () => {
    if (!userId) return;
    const url = buildWsUrl();
    if (!url) {
      setStatus('error');
      return;
    }

    try {
      setStatus('connecting');
      const ws = new WebSocket(url);
      wsRef.current = ws;

      ws.onopen = () => {
        setIsConnected(true);
        setStatus('connected');
        reconnectAttemptsRef.current = 0;
      };

      ws.onmessage = (event) => {
        try {
          const data: IncomingMessage = JSON.parse(event.data);
          switch (data.type) {
            case 'subscription_confirmed':
              // Подписка подтверждена
              break;
            case 'unsubscription_confirmed':
              // Отписка подтверждена
              break;
            case 'unread_count_update':
              if (data.bot_name && typeof data.count === 'number') {
                onUnreadCountUpdate?.(data.bot_name, data.count);
              }
              break;
            case 'new_message':
              if (data.bot_name) {
                onNewMessage?.(data);
              }
              break;
            case 'access_denied':
              // Доступ запрещен
              break;
            case 'pong':
              // Игнорируем pong
              break;
            default:
              // Неизвестный тип сообщения
              break;
          }
        } catch (e) {
          // игнорируем парсинг ошибки
        }
      };

      ws.onerror = () => {
        setStatus('error');
      };

      ws.onclose = (event) => {
        setIsConnected(false);
        setStatus('disconnected');
        if (event.code !== 1000 && reconnectAttemptsRef.current < maxReconnectAttempts) {
          const delay = Math.min(1000 * Math.pow(2, reconnectAttemptsRef.current), 10000);
          reconnectAttemptsRef.current++;
          reconnectTimeoutRef.current = setTimeout(() => connect(), delay);
        } else if (reconnectAttemptsRef.current >= maxReconnectAttempts) {
          setStatus('error');
        }
      };
    } catch (e) {
      setStatus('error');
    }
  }, [userId]);

  useEffect(() => {
    if (userId) {
      connect();
    }
    return () => {
      if (reconnectTimeoutRef.current) {
        clearTimeout(reconnectTimeoutRef.current);
      }
      if (wsRef.current) {
        try { wsRef.current.close(1000, 'cleanup'); } catch (e) {}
      }
      wsRef.current = null;
      setIsConnected(false);
      setStatus('disconnected');
      reconnectAttemptsRef.current = 0;
    };
  }, [userId, connect]);

  return { status, isConnected, subscribe, unsubscribe, markRead };
};


